import { useCallback, useEffect, useRef, useState } from 'react';

import { Tracking } from '@skyscanner/strevda-tracking';
import BpkBreakpoint, {
  BREAKPOINTS,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkButton, {
  BUTTON_TYPES,
  SIZE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import { withButtonAlignment } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import LocationIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/location';
import BpkIconCloseSm from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/close';
import BpkPauseIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/pause';
import BpkPlayIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/play';
import BpkSpeakerIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/speaker';
import BpkSpeakerMuteIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/speaker-mute';
import BpkOverlay, { OVERLAY_TYPES } from '@skyscanner-internal/backpack-web/bpk-component-overlay';
import BpkText, { TEXT_STYLES } from '@skyscanner-internal/backpack-web/bpk-component-text';
import { withDefaultProps } from '@skyscanner-internal/backpack-web/bpk-react-utils';
import { VideoPlayer } from '@skyscanner-internal/global-components/video-player';
import ExpandableDescription from '@web-platform/shared-banana-features-adverts/src/ExpandableDescription/ExpandableDescription';
import {
  ERROR_BOUNDARY_PREFIX_QUARK,
  ADVERT_ENGAGEMENT_TYPES,
} from '@web-platform/shared-universal-constants/src/adverts';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';
import {
  SponsoredInfoButton,
  getSponsoredCallToActionData,
} from '@web-platform/shared-universal-features-adverts';
import {
  createVideoPlayerLogger,
  warmHlsManifest,
} from '@web-platform/shared-universal-features-video-utils-shared';
import { getUserDevice } from '@web-platform/shared-universal-redux/src/state';
import { getStrevdaTrackingConfig } from '@web-platform/shared-universal-tracking/src/getStrevdaTrackingConfig';
import { trackInteractionEvent } from '@web-platform/shared-universal-tracking/src/trackInteractionEvent/trackInteractionEvent';
import { translate } from '@web-platform/shared-universal-translations';
import ErrorBoundary from '@web-platform/shared-universal-ui-error-boundary';
import {
  logWarn,
  logOperationalEvent,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { getCulture } from '@web-platform/shared-universal-utils-frontend-localisation';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { useSelector } from 'react-redux';

import { useLegacyVideoTracking } from './useLegacyVideoTracking';
import { PLAYHEAD_MILESTONES, useSpicyVideoTracking } from './useSpicyVideoTracking';

import type { HomepageVideo as HomepageVideoAdvert } from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entitiesSearchResults/adverts/homepageVideo';

import STYLES from './HomepageVideo.module.scss';

const AlignedSpeakerIcon = withButtonAlignment(BpkSpeakerIcon);
const AlignedPauseIcon = withButtonAlignment(BpkPauseIcon);
const AlignedPlayIcon = withButtonAlignment(BpkPlayIcon);
const AlignedSpeakerMuteIcon = withButtonAlignment(BpkSpeakerMuteIcon);
const AlignedCloseIcon = withButtonAlignment(BpkIconCloseSm);
const IconButton = withDefaultProps(BpkButton, {
  iconOnly: true,
  type: BUTTON_TYPES.secondaryOnDark,
});
const RedirectTextButton = withDefaultProps(BpkButton, {
  type: BUTTON_TYPES.secondary,
  size: SIZE_TYPES.large,
  className: STYLES.redirectButton,
});

const videoPlayerLogger = createVideoPlayerLogger('HomepageVideoPlayer');

export type HomepageVideoProps = {
  advert: HomepageVideoAdvert;
  onClick: (mediaType: 'image' | 'video') => void;
  creativeId: string;
  /** Swaps the media element for Spicy's `VideoPlayer`. Everything around it is shared. */
  useSpicyVideoPlayer: boolean;
};

type PlayPauseButtonProps = {
  onClick: () => void;
  isPaused: boolean;
};

const PlayPauseButton = ({ isPaused, onClick }: PlayPauseButtonProps) => (
  <IconButton
    aria-label={isPaused ? translate('aria_video_play') : translate('aria_video_pause')}
    onClick={onClick}
  >
    {isPaused ? <AlignedPlayIcon /> : <AlignedPauseIcon />}
  </IconButton>
);

type MuteButtonProps = {
  isMuted: boolean;
  onClick: () => void;
};

const MuteButton = ({ isMuted, onClick }: MuteButtonProps) => (
  <IconButton
    aria-label={isMuted ? translate('aria_video_unmute') : translate('aria_video_mute')}
    onClick={onClick}
  >
    {isMuted ? <AlignedSpeakerMuteIcon /> : <AlignedSpeakerIcon />}
  </IconButton>
);

type CloseButtonProps = {
  onClick: () => void;
};

const CloseButton = ({ onClick }: CloseButtonProps) => (
  <IconButton aria-label={translate('aria_close_button')} onClick={onClick}>
    <AlignedCloseIcon />
  </IconButton>
);

type PartnerAttributionProps = {
  partnerLogo: string;
  partnerName: string;
  pioPartnerName: string;
  pioAgencyName?: string;
};

const PartnerAttribution = ({
  partnerLogo,
  partnerName,
  pioAgencyName,
  pioPartnerName,
}: PartnerAttributionProps) => {
  const callToActionData = getSponsoredCallToActionData({ pioPartnerName, pioAgencyName });

  return (
    <div className={STYLES.subheadingContainer}>
      <img src={partnerLogo} alt="" className={STYLES.logo} />
      <div className={STYLES.sponsoredButton}>
        <SponsoredInfoButton infoIconCustomColor callToAction={callToActionData}>
          <BpkText textStyle={TEXT_STYLES.caption}>
            {translate('homepageHero_in_partnership_with', {
              partner: partnerName,
            })}
          </BpkText>
        </SponsoredInfoButton>
      </div>
    </div>
  );
};

export const HomepageVideo = ({
  advert,
  creativeId,
  onClick,
  useSpicyVideoPlayer,
}: HomepageVideoProps) => {
  const { creative } = advert;
  const {
    ctaCopy,
    headline,
    message,
    partnerLogo,
    partnerName,
    pioAgencyName,
    pioPartnerName,
    videoCountry,
    videoLocation,
    videoPosterImage,
    videoUrl,
  } = creative;

  const androidFullScreen = useFullScreenHandle();
  const { osName, prefersReducedMotion = false } = useSelector(getUserDevice);
  const isIOS = (): boolean => /iOS/i.test(osName);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  // VideoPlayer exposes no ref to its internal <video>, so reach it via the container.
  const getVideo = (): HTMLVideoElement | null =>
    useSpicyVideoPlayer
      ? (videoContainerRef.current?.querySelector('video') ?? null)
      : videoRef.current;
  const [isIOSFullScreen, setIsIOSFullScreen] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [isPaused, setIsPaused] = useState<boolean>(prefersReducedMotion);
  const [isInitialFullScreenChange, setIsInitialFullScreenChange] = useState<boolean>(true);
  const [isReadyToPlay, setIsReadyToPlay] = useState<boolean>(false);
  const isFullScreen = isIOSFullScreen || androidFullScreen.active;
  // useSpicyVideoPlayer is resolved once per request server-side (see
  // getFeatureDecision) and baked into the page at bootstrap — nothing in the codebase
  // currently mutates it live, so it can't change without a full remount. If that ever
  // changes, this ternary would violate the Rules of Hooks; watch for that if a live
  // feature-decision refresh is ever introduced.
  /* eslint-disable react-hooks/rules-of-hooks */
  const tracking = useSpicyVideoPlayer
    ? useSpicyVideoTracking({ creativeId, isFullScreen })
    : useLegacyVideoTracking({ creativeId, isFullScreen, getVideo });
  /* eslint-enable react-hooks/rules-of-hooks */

  // Video HTML5 readyState threshold: >= 2 means video has loaded and is playable
  const VIDEO_READY_THRESHOLD = 2;

  const getVisibleMediaType = (): 'image' | 'video' => {
    // No readyState via the VideoPlayer API, so onReady is the only signal (QUAR-1783).
    if (useSpicyVideoPlayer) {
      return isReadyToPlay ? 'video' : 'image';
    }
    const readyState = getVideo()?.readyState ?? 0;

    // Use isReadyToPlay gatekeeper to handle slow network readyState transitions
    const mediaType = isReadyToPlay && readyState >= VIDEO_READY_THRESHOLD ? 'video' : 'image';

    return mediaType;
  };

  const onClickWrapper = (event: React.MouseEvent<Element, MouseEvent>) => {
    event.stopPropagation();
    const visibleMediaType = getVisibleMediaType();

    // Enter fullscreen before tracking to preserve transient user activation on Android Chrome
    isReadyToPlay ? onEnterFullScreen() : onClick(visibleMediaType);
    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_CARD_TAPPED.NAME,
      strings: {
        creativeId,
      },
    });
    logOperationalEvent({
      eventName: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_CARD_TAPPED.NAME,
      lineItemId: creativeId,
    });
  };

  const handlePause = () => {
    // Captured here, before pause()'s deferred native event lands, so a player-sourced
    // onPause callback (Spicy) tags the event with whatever was true when the pause was
    // initiated, not whenever the async event happens to fire.
    tracking.notePauseInitiated(isFullScreen);
    getVideo()?.pause();
    setIsPaused(true);
    tracking.onPause?.();
  };

  const handlePlay = () => {
    getVideo()
      ?.play()
      .catch(() => {});
    setIsPaused(false);
  };

  const hasLoggedPlay = useRef(false);
  const hasRetriedRef = useRef(false);
  const [retryToken, setRetryToken] = useState(0);

  // `muted` is mount-only on VideoPlayer, so toggling it has to be applied imperatively.
  useEffect(() => {
    if (!useSpicyVideoPlayer) {
      return;
    }
    const video = getVideo();
    if (video) {
      video.muted = isMuted;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMuted, useSpicyVideoPlayer]);

  // Warms the browser's HTTP cache for the manifest before VideoPlayer mounts and
  // requests it itself, reducing manifest/fragment load timeouts on cold network
  // conditions
  useEffect(() => {
    if (useSpicyVideoPlayer && videoUrl.endsWith('.m3u8')) {
      warmHlsManifest(videoUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isReadyToPlay && !isPaused && !hasLoggedPlay.current) {
      logOperationalEvent({
        eventName: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_PLAYED.NAME,
        lineItemId: creativeId,
      });
      hasLoggedPlay.current = true;
    }
  }, [isPaused, isReadyToPlay, creativeId]);

  useEffect(() => {
    hasLoggedPlay.current = false;
    hasRetriedRef.current = false;
  }, [creativeId]);

  const handleReady = useCallback(() => {
    tracking.onPlayerReady?.();
    if (!tracking.didTimeOut()) {
      setIsReadyToPlay(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracking.onPlayerReady, tracking.didTimeOut]);

  // If the video already played once, any error afterwards is more likely a late-stage
  // hiccup than a load failure, so it's always logged as a warning without remounting or
  // re-triggering the poster fallback — checked first so it takes priority over the retry
  // below on every post-play error, not just a second one.
  // Otherwise, most Safari/iOS VideoPlayer errors before the video has ever played are
  // transient (offscreen teardown, network jitter) rather than genuine playback failures,
  // so the first such error remounts the player once via `retryToken` before giving up.
  // onPlayerRetry restarts the fallback-timeout window for the retried mount, so it isn't
  // racing against whatever time was left on the original mount's timer.
  const handleError = useCallback(() => {
    if (hasLoggedPlay.current) {
      logWarn({
        component: 'HomepageVideoPlayer',
        message: 'VideoPlayer error after successful play',
        creativeId,
      });
      return;
    }
    if (!hasRetriedRef.current) {
      hasRetriedRef.current = true;
      tracking.onPlayerRetry?.();
      setRetryToken((token) => token + 1);
      return;
    }
    tracking.onPlayerError?.();
    setIsReadyToPlay(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracking.onPlayerError, tracking.onPlayerRetry, creativeId]);

  const toggleIsPlaying = () => {
    isPaused ? handlePlay() : handlePause();
  };

  const onRedirectClickWrapper = () => {
    tracking.onRedirect?.();
    onClick(getVisibleMediaType());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Spacebar' || e.key === ' ' || e.key === 'space') {
      e.preventDefault();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      isReadyToPlay ? onEnterFullScreen() : onClick(getVisibleMediaType());
    }
  };
  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === 'Spacebar' || e.key === ' ' || e.key === 'space') {
      e.preventDefault();
      isReadyToPlay ? onEnterFullScreen() : onClick(getVisibleMediaType());
    }
  };

  const handleOnEnded = () => {
    tracking.onEnded?.(isFullScreen);
    handlePlay();
  };

  const onEnterFullScreen = () => {
    if (isFullScreen) {
      return;
    }
    tracking.onEnterFullscreen();
    const visibleMediaType = getVisibleMediaType();

    if (isIOS()) {
      setIsIOSFullScreen(true);
      Tracking.trackEngagement(
        advert,
        ADVERT_ENGAGEMENT_TYPES.VIDEO_OPEN,
        visibleMediaType,
        getStrevdaTrackingConfig(),
      );
      trackInteractionEvent({
        name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_FULLSCREEN_VIEWED.NAME,
        strings: {
          creativeId,
          mediaType: visibleMediaType,
        },
      });
      logOperationalEvent({
        eventName: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_FULLSCREEN_VIEWED.NAME,
        lineItemId: creativeId,
        mediaType: visibleMediaType,
      });
      setIsMuted(true);
      handlePlay();
    } else {
      // Call requestFullscreen immediately to preserve transient user activation on Android Chrome.
      // Tracking and orientation lock are deferred to .then() since they don't require activation.
      try {
        androidFullScreen
          .enter()
          .then(() => {
            Tracking.trackEngagement(
              advert,
              ADVERT_ENGAGEMENT_TYPES.VIDEO_OPEN,
              visibleMediaType,
              getStrevdaTrackingConfig(),
            );
            trackInteractionEvent({
              name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_FULLSCREEN_VIEWED.NAME,
              strings: {
                creativeId,
                mediaType: visibleMediaType,
              },
            });
            logOperationalEvent({
              eventName: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_VIDEO_AD_FULLSCREEN_VIEWED.NAME,
              lineItemId: creativeId,
              mediaType: visibleMediaType,
            });
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // eslint-disable-next-line no-restricted-globals
            if ('orientation' in screen && 'lock' in screen.orientation) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              // eslint-disable-next-line no-restricted-globals
              screen.orientation

                .lock('portrait-primary')
                // eslint-disable-next-line promise/no-nesting
                .then(() => {})
                // eslint-disable-next-line promise/no-nesting
                .catch(() => {
                  // Orientation lock may fail as requestFullscreen consumes user activation
                });
            }
            setIsMuted(true);
            handlePlay();
          })
          .catch((error) => {
            logOperationalEvent({
              eventName: 'Failed to enter Android fullscreen',
              component: `${ERROR_BOUNDARY_PREFIX_QUARK}HomepageVideo`,
              error: error.toString(),
              lineItemId: creativeId,
            });
          });
      } catch (error) {
        logOperationalEvent({
          eventName: 'Failed to enter Android fullscreen',
          component: `${ERROR_BOUNDARY_PREFIX_QUARK}HomepageVideo`,
          error: error.toString(),
          lineItemId: creativeId,
        });
      }
    }
  };

  const onExitFullScreen = () => {
    tracking.onExitFullscreen?.();
    setIsMuted(true);
    prefersReducedMotion ? handlePause() : handlePlay();
    if (isIOS()) {
      setIsIOSFullScreen(false);
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line no-restricted-globals
      if ('orientation' in screen && 'unlock' in screen.orientation) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line no-restricted-globals
        screen.orientation.unlock();
      }

      androidFullScreen.exit();
    }
  };

  const handleFullScreenChange = (newState: boolean) => {
    if (isInitialFullScreenChange) {
      setIsInitialFullScreenChange(false);
      return;
    }

    if (isIOSFullScreen) {
      onExitFullScreen();
      return;
    }

    newState ? onEnterFullScreen() : onExitFullScreen();
  };

  // BpkButton (via IconButton) doesn't forward refs, so the wrapper div is refed instead
  // and the native <button> reached via querySelector when focus needs to move to it.
  const closeButtonWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isFullScreen) {
      closeButtonWrapperRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    }
  }, [isFullScreen]);

  const videoClassName = isFullScreen ? STYLES.videoFullscreen : STYLES.videoSmall;
  const getVideoContainerClassName = (isAboveMobile: boolean) => {
    if (isFullScreen) {
      return STYLES.containerFullscreen;
    }
    if (isAboveMobile) {
      return STYLES.containerSmallLandscape;
    }
    return STYLES.containerSmall;
  };

  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const toggleDescription = (expanded: boolean) => {
    setIsDescriptionExpanded(expanded);
  };

  const videoContainerAriaLabel = `${headline}. ${translate('homepageHero_in_partnership_with', {
    partner: partnerName,
  })}`;
  const videoLocationAriaLabel = videoLocation + (videoCountry ? `, ${videoCountry}` : '');

  return (
    <ErrorBoundary
      componentName={`${ERROR_BOUNDARY_PREFIX_QUARK}HomepageVideo`}
      fallback={null}
      onError={(errorData) => logWarn({ ...errorData, message: 'Error caught by error boundary' })}
    >
      <div className={STYLES.homepageVideoContainer}>
        <FullScreen
          handle={androidFullScreen}
          // eslint-disable-next-line @skyscanner/rules/forbid-component-props
          className={STYLES.fullscreen}
          onChange={handleFullScreenChange}
        >
          <section aria-label={videoContainerAriaLabel}>
            <BpkBreakpoint query={BREAKPOINTS.ABOVE_MOBILE}>
              {(isAboveMobile: boolean) => (
                <div className={getVideoContainerClassName(isAboveMobile)}>
                  {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                  <div
                    data-testid="homepage-video-surface"
                    ref={videoContainerRef}
                    className={videoClassName}
                    onClick={onClickWrapper}
                    onContextMenu={(e) => e.preventDefault()}
                  >
                    {useSpicyVideoPlayer ? (
                      <VideoPlayer
                        // Bumping retryToken remounts VideoPlayer for a one-shot retry on error.
                        key={`${creativeId}-${retryToken}`}
                        videoSrc={videoUrl}
                        posterSrc={videoPosterImage}
                        controls={false}
                        muted={isMuted}
                        subtitles={false}
                        autoplay={!prefersReducedMotion || isFullScreen}
                        loop
                        playsInline
                        locale={getCulture().locale}
                        objectFit="cover"
                        onReady={handleReady}
                        onError={handleError}
                        onPause={tracking.onPlayerPause}
                        playheadMilestones={PLAYHEAD_MILESTONES}
                        onTimeUpdate={tracking.onPlayerTimeUpdate}
                        logger={videoPlayerLogger}
                        pauseWhenOffscreen
                      />
                    ) : (
                      /* eslint-disable-next-line jsx-a11y/media-has-caption */
                      <video
                        data-testid="video"
                        src={videoUrl}
                        poster={videoPosterImage}
                        playsInline
                        autoPlay={!prefersReducedMotion || isFullScreen}
                        muted={isMuted}
                        ref={videoRef}
                        onLoadStart={tracking.onLoadStart}
                        onCanPlayThrough={() => {
                          const timedOut = tracking.onCanPlayThrough?.() ?? false;
                          if (!timedOut) {
                            setIsReadyToPlay(true);
                          } else if (videoRef.current?.src && !isReadyToPlay) {
                            videoRef.current.src = '';
                            setIsReadyToPlay(false);
                          }
                        }}
                        onEnded={handleOnEnded}
                      />
                    )}
                    {/* VideoPlayer suppresses its own poster while autoplaying, so
                        `posterSrc` alone is not a usable fallback. Legacy uses its
                        native `poster` attribute instead. */}
                    {useSpicyVideoPlayer && !isReadyToPlay && videoPosterImage && (
                      <img
                        src={videoPosterImage}
                        alt=""
                        aria-hidden="true"
                        className={STYLES.fallbackImage}
                      />
                    )}
                  </div>
                  <span>
                    {isFullScreen ? (
                      <>
                        <BpkOverlay
                          overlayType={OVERLAY_TYPES.heavyTop}
                          className={STYLES.heavyOverlayTop}
                          foregroundContent={
                            <div className={STYLES.topContentHolder}>
                              <div
                                className={STYLES.closeButtonWrapper}
                                ref={closeButtonWrapperRef}
                              >
                                <CloseButton onClick={onExitFullScreen} />
                              </div>
                              <div
                                className={STYLES.locationWrapper}
                                aria-label={videoLocationAriaLabel}
                              >
                                <LocationIcon fill="white" />
                                <div className={STYLES.locationContainer} aria-hidden="true">
                                  <BpkText
                                    textStyle={TEXT_STYLES.heading5}
                                  >{`${videoLocation}${videoCountry ? ',' : ''}`}</BpkText>
                                  <BpkText weight="bold">{videoCountry}</BpkText>
                                </div>
                              </div>
                            </div>
                          }
                        >
                          {/* Overlay requires a child component */}
                          {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
                          <></>
                        </BpkOverlay>
                        <BpkOverlay
                          overlayType={OVERLAY_TYPES.heavyBottom}
                          className={`${STYLES.heavyOverlayBottom} ${
                            isDescriptionExpanded ? STYLES.expanded : ''
                          }`}
                          foregroundContent={
                            <div className={STYLES.bottomContentHolderFullscreen}>
                              <div className={STYLES.buttonAndText}>
                                {message && (
                                  <div className={STYLES.textHolder}>
                                    <ExpandableDescription
                                      description={message}
                                      maximumLength={50}
                                      buttonStyle={{ type: BUTTON_TYPES.linkOnDark }}
                                      expandedClassName={STYLES.expandedDescription}
                                      onExpand={toggleDescription}
                                      isExpanded={isDescriptionExpanded}
                                      showLess
                                    />
                                  </div>
                                )}
                                <div className={STYLES.playMuteButtonHolder}>
                                  <PlayPauseButton onClick={toggleIsPlaying} isPaused={isPaused} />
                                  <MuteButton
                                    onClick={() => setIsMuted((current) => !current)}
                                    isMuted={isMuted}
                                  />
                                </div>
                              </div>
                              <PartnerAttribution
                                partnerLogo={partnerLogo}
                                partnerName={partnerName}
                                pioPartnerName={pioPartnerName}
                                pioAgencyName={pioAgencyName}
                              />
                              <div className={STYLES.redirectButtonHolder}>
                                <RedirectTextButton onClick={onRedirectClickWrapper}>
                                  {ctaCopy}
                                </RedirectTextButton>
                              </div>
                            </div>
                          }
                        >
                          {/* Overlay requires a child component */}
                          {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
                          <></>
                        </BpkOverlay>
                      </>
                    ) : (
                      <BpkOverlay
                        overlayType={OVERLAY_TYPES.bottomHigh}
                        className={STYLES.overlay}
                        foregroundContent={
                          <div className={STYLES.bottomContentHolderSmall}>
                            <BpkText
                              aria-hidden="true"
                              textStyle={TEXT_STYLES.heading2}
                              onClick={onClickWrapper}
                            >
                              {headline}
                            </BpkText>
                            <PartnerAttribution
                              partnerLogo={partnerLogo}
                              partnerName={partnerName}
                              pioPartnerName={pioPartnerName}
                              pioAgencyName={pioAgencyName}
                            />
                          </div>
                        }
                      >
                        {/* Overlay requires a child component */}
                        {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
                        <></>
                      </BpkOverlay>
                    )}
                  </span>
                </div>
              )}
            </BpkBreakpoint>
            <div />
          </section>
        </FullScreen>
        <button
          type="button"
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onClick={onClickWrapper}
          className={STYLES.fullscreenButton}
          aria-label={translate('aria_video_open')}
        />
        {isReadyToPlay && !isFullScreen && (
          <div className={STYLES.topButtonHolder}>
            <PlayPauseButton onClick={toggleIsPlaying} isPaused={isPaused} />
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};
